import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import PageBanner from '@/components/shared/PageBanner';
import TestimonialCard from '@/components/shared/TestimonialCard';
import CTABanner from '@/components/shared/CTABanner';
import { DCA_LINKS } from '@/lib/dcaConfig';
import { publicApi } from '@/lib/api';

const AboutPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.getTestimonials()
      .then(res => setTestimonials(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const strategistFeatures = [
    'Gain Clarity',
    'Overcome Barriers',
    'Develop Skills',
    'Explore and Discover',
    'Achieve More',
    'Purpose Aligned Career'
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        <PageBanner 
          title="About" 
          breadcrumbs={[{ label: 'About' }]} 
        />

        {/* Mission Statement */}
        <section className="py-20 bg-primary text-white" data-testid="mission-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold">
              SEG Career Support helps individuals to discover their career aligned purpose by assisting with all areas of Career development.
            </h2>
          </div>
        </section>

        {/* Career Strategist Section */}
        <section className="py-20" data-testid="about-strategist-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
                  Career Strategist
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Let me help you navigate the search for a better job faster, develop your leadership potential, 
                  transition into a new career, and assist your downsized employees with their career transition. 
                  I provide professional guidance, tools and a plan so you have a clear direction and no detours to slow you down.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {strategistFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-primary" />
                      <span className="text-sm font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
                <Button size="lg" data-testid="about-contact-btn" onClick={() => window.open(DCA_LINKS.free20, '_blank', 'noopener,noreferrer')}>
                  Free 20-Minute Career Strategy Session <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_7df1c564-588e-4a17-acd3-02a3087015ac/artifacts/on6p2z7g__DSA0398BSC.jpg"
                    alt="David Ashley - Career Strategist"
                    className="w-full h-auto"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-primary rounded-xl"></div>
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-secondary rounded-xl"></div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <CTABanner />

        {/* Testimonials Section */}
        <section className="py-20 bg-muted/30" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">What Clients Say</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Hear from professionals who have transformed their careers with our guidance
              </p>
            </div>

            {/* Testimonials Carousel */}
            <div className="relative">
              <div className="grid md:grid-cols-3 gap-8">
                {testimonials.map((testimonial, index) => (
                  <div
                    key={testimonial.id}
                    className={`transition-all duration-500 ${
                      index === currentTestimonial || window.innerWidth >= 768 
                        ? 'opacity-100 translate-x-0' 
                        : 'opacity-0 absolute'
                    }`}
                  >
                    <TestimonialCard testimonial={testimonial} />
                  </div>
                ))}
              </div>

              {/* Mobile Navigation */}
              {testimonials.length > 1 && (
                <div className="flex justify-center gap-4 mt-8 md:hidden">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={prevTestimonial}
                    data-testid="testimonial-prev"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <div className="flex items-center gap-2">
                    {testimonials.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentTestimonial(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentTestimonial ? 'bg-primary w-6' : 'bg-muted-foreground/30'
                        }`}
                      />
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={nextTestimonial}
                    data-testid="testimonial-next"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
